
import React, { useState, useEffect } from 'react';
import { X, History, Eye, ArrowUpRight, ArrowDownRight, Trash2, Plus, Edit2 } from 'lucide-react';
import { Transaction } from '../types';

interface TransactionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transactions: Transaction[];
  symbol: string;
  theme: 'dark' | 'light';
  selectedId?: string | null;
  onSelectTransaction: (id: string | null) => void;
  currentPrice: number;
  onDeleteTransaction?: (id: string) => void;
  onAddTransaction?: () => void;
  onEditTransaction?: (transaction: Transaction) => void;
  fetchCoinMetadata?: (symbols: string[], typeMap?: Record<string, string>) => Promise<Record<string, string>>;
}

const TransactionsModal: React.FC<TransactionsModalProps> = ({ 
  isOpen, onClose, transactions, symbol, theme, selectedId, onSelectTransaction, currentPrice, onDeleteTransaction, onAddTransaction, onEditTransaction, fetchCoinMetadata 
}) => {
  const [icons, setIcons] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen && fetchCoinMetadata) {
      const symbols = Array.from(new Set(transactions.map(t => t.ticket)));
      const typeMap: Record<string, string> = {};
      
      transactions.forEach(t => {
         const base = t.ticket.split(/[-/]/)[0].toUpperCase();
         typeMap[base] = t.category || 'criptomoneda';
      });

      fetchCoinMetadata(symbols, typeMap).then(setIcons);
    }
  }, [isOpen, transactions, fetchCoinMetadata]);

  if (!isOpen) return null;

  const getIconUrl = (symbol: string) => {
    const base = symbol.split(/[-/]/)[0].toUpperCase();
    return icons[base] || '';
  };

  const filtered = transactions
    .filter(t => {
      const tBase = t.ticket.split(/[-/]/)[0].toUpperCase();
      const sBase = symbol.split(/[-/]/)[0].toUpperCase();
      return tBase === sBase;
    })
    .sort((a, b) => b.timestamp - a.timestamp);

  const themeClasses = {
    overlay: "fixed inset-0 bg-black/80 backdrop-blur-sm z-[110] flex items-center justify-center p-4 animate-in fade-in duration-200",
    container: `${theme === 'dark' ? 'bg-[#0f0f0f] border-[#262626]' : 'bg-white border-slate-200'} border w-full max-w-5xl h-[80vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200`,
    header: `flex items-center justify-between p-4 border-b ${theme === 'dark' ? 'border-[#262626]' : 'border-slate-100'}`,
    tableHeader: `${theme === 'dark' ? 'bg-[#1a1a1a] text-slate-400' : 'bg-slate-50 text-slate-500'} text-xs font-black uppercase tracking-wider sticky top-0 z-10`,
    cell: `px-4 py-3 text-sm font-medium border-b ${theme === 'dark' ? 'border-[#1a1a1a]' : 'border-slate-50'}`,
    textMain: theme === 'dark' ? 'text-white' : 'text-slate-900',
    rowHover: theme === 'dark' ? 'hover:bg-[#1e1e1e]' : 'hover:bg-slate-50',
    selected: theme === 'dark' ? 'bg-sky-400/20' : 'bg-indigo-50'
  };

  return (
    <div className={themeClasses.overlay} onClick={onClose}>
      <div className={themeClasses.container} onClick={e => e.stopPropagation()}>
        <div className={themeClasses.header}>
          <div className="flex items-center space-x-3">
            <div className="bg-indigo-600 p-1.5 rounded-lg shadow-lg">
              <History size={18} className="text-white" />
            </div>
            <div>
              <h2 className={`text-base font-black ${themeClasses.textMain} tracking-tight uppercase`}>Historial: {symbol}</h2>
              <p className="text-slate-500 text-[8px] font-bold uppercase">Transacciones realizadas</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {onAddTransaction && (
              <button 
                onClick={onAddTransaction} 
                className="flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase shadow-lg shadow-indigo-600/20 transition-all"
              >
                <Plus size={14} />
                <span>AÑADIR TRANSACCIÓN</span>
              </button>
            )}
            <button onClick={onClose} className={`p-1.5 rounded-full ${theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-slate-100'} transition-colors`}>
              <X size={18} className={themeClasses.textMain} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <table className="w-full text-left border-collapse">
            <thead className={themeClasses.tableHeader}>
              <tr>
                <th className="px-4 py-3 w-10"></th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Precio Adq.</th>
                <th className="px-4 py-3">Cantidad</th>
                <th className="px-4 py-3">P&G (PnL)</th>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className={theme === 'dark' ? 'divide-[#1a1a1a]' : 'divide-slate-50'}>
              {filtered.map((t) => {
                const isBuy = t.status?.toLowerCase().includes('compra') || t.status?.toLowerCase().includes('buy');
                const isSelected = selectedId === t.id;
                
                const pnl = isBuy && currentPrice > 0 ? (currentPrice - t.price) * t.quantity : 0;
                const pnlPerc = isBuy && t.price > 0 ? ((currentPrice - t.price) / t.price) * 100 : 0;

                return (
                  <tr 
                    key={t.id} 
                    onClick={() => onSelectTransaction(isSelected ? null : t.id)}
                    className={`cursor-pointer transition-colors ${isSelected ? themeClasses.selected : themeClasses.rowHover}`}
                  >
                    <td className="px-4 py-3 align-middle"><div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black relative flex-shrink-0 overflow-hidden ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}><span>{t.ticket.charAt(0)}</span>{getIconUrl(t.ticket) && <img src={getIconUrl(t.ticket)} className="absolute inset-0 w-full h-full object-contain bg-inherit" alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}</div></td>
                    <td className={themeClasses.cell}>
                      <span className={`font-black uppercase text-xs ${isBuy ? 'text-[#089981]' : 'text-[#f23645]'}`}>
                        {isBuy ? 'Compra' : 'Venta'}
                      </span>
                    </td>
                    <td className={`${themeClasses.cell} font-mono ${themeClasses.textMain}`}>
                      $ {t.price.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className={`${themeClasses.cell} font-mono ${themeClasses.textMain}`}>
                      {t.quantity.toLocaleString(undefined, { maximumFractionDigits: 8 })}
                    </td>
                    <td className={themeClasses.cell}>
                      {isBuy && currentPrice > 0 ? (
                        <div className={`flex flex-col font-mono ${pnl >= 0 ? 'text-[#089981]' : 'text-[#f23645]'}`}>
                          <div className="flex items-center font-black">
                            {pnl >= 0 ? <ArrowUpRight size={12} className="mr-1" /> : <ArrowDownRight size={12} className="mr-1" />}
                            ${Math.abs(pnl).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div className="text-xs opacity-70 font-bold">
                            {pnl >= 0 ? '+' : '-'}{Math.abs(pnlPerc).toFixed(2)}%
                          </div>
                        </div>
                      ) : <span className="text-slate-500 text-xs">-</span>}
                    </td>
                    <td className={themeClasses.cell}>
                      <div className="flex flex-col">
                        <span className={`font-black ${themeClasses.textMain}`}>{t.date}</span>
                        <span className="text-xs text-slate-500 font-mono">{t.time}</span>
                      </div>
                    </td>
                    <td className={`${themeClasses.cell} text-right font-black font-mono ${themeClasses.textMain}`}>
                      $ {t.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className={`${themeClasses.cell} text-center`}>
                      <div className="flex items-center justify-center space-x-2">
                        <button 
                          className={`p-1.5 rounded-full ${isSelected ? 'bg-sky-500 text-white' : 'text-slate-500'}`}
                        >
                          <Eye size={14} />
                        </button>
                        {onEditTransaction && (
                          <button 
                            onClick={(e) => { e.stopPropagation(); onEditTransaction(t); }}
                            className="p-1.5 rounded-full text-slate-500 hover:bg-sky-500/10 hover:text-sky-500 transition-colors"
                            title="Editar Transacción"
                          >
                            <Edit2 size={14} />
                          </button>
                        )}
                        {onDeleteTransaction && (
                          <button 
                            onClick={(e) => { e.stopPropagation(); onDeleteTransaction(t.id); }}
                            className="p-1.5 rounded-full text-slate-500 hover:bg-rose-500/10 hover:text-rose-500 transition-colors"
                            title="Eliminar Transacción"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-20 text-center text-slate-500 uppercase font-black text-[10px] tracking-widest">
                    Sin transacciones registradas para este activo
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TransactionsModal;
